/**
 * modals/PlaybookEditModal.tsx
 * Modal for adding/editing playbook basic information
 *
 * Handles playbook name, description, and philosophy fields.
 * Task 1 scope: Basic info only. Tasks 2 & 3 will add pickers for criteria/confluences/models.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { BookOpen } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, TextArea } from '@enfomentis/ui';

// 3. Relative imports
import type { PlaybookDraft } from '../hooks/usePlaybookBuilder';

/**
 * Props for PlaybookEditModal
 */
interface PlaybookEditModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Mode: add or edit */
  mode: 'add' | 'edit';
  /** Existing playbook data (for edit mode) */
  existingData?: PlaybookDraft | undefined;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for saving playbook data */
  onSave: (data: { name: string; description?: string; philosophy?: string }) => void;
}

/**
 * Playbook edit modal component
 */
export const PlaybookEditModal: React.FC<PlaybookEditModalProps> = ({
  isOpen,
  mode,
  existingData,
  onClose,
  onSave
}) => {
  // Local form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    philosophy: ''
  });

  // Initialize form from existing data when in edit mode
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && existingData) {
        setFormData({
          name: existingData.name,
          description: existingData.description || '',
          philosophy: existingData.philosophy || ''
        });
      } else {
        // Reset for add mode
        setFormData({
          name: '',
          description: '',
          philosophy: ''
        });
      }
    }
  }, [isOpen, mode, existingData]);

  /**
   * Handle form submission
   */
  const handleSave = (): void => {
    if (!formData.name.trim()) {
      return; // Name is required
    }

    const saveData: { name: string; description?: string; philosophy?: string } = {
      name: formData.name.trim()
    };

    if (formData.description.trim()) {
      saveData.description = formData.description.trim();
    }

    if (formData.philosophy.trim()) {
      saveData.philosophy = formData.philosophy.trim();
    }

    onSave(saveData);
  };

  /**
   * Handle cancel
   */
  const handleCancel = (): void => {
    onClose();
  };

  const isNameValid = formData.name.trim().length >= 2;
  const canSave = isNameValid;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              <BookOpen className="w-5 h-5 text-primary" />
              <span>{mode === 'add' ? 'Add New Playbook' : 'Edit Playbook'}</span>
            </ModalHeader>

            <ModalBody className="gap-4">
          {/* Playbook Name */}
          <Input
            label="Playbook Name"
            placeholder="e.g., Opening Range Breakout, Mean Reversion"
            value={formData.name}
            onValueChange={(value) => setFormData({ ...formData, name: value })}
            isRequired
            variant="bordered"
            size="lg"
            description="Give your playbook a memorable name"
            errorMessage={
              formData.name.length > 0 && !isNameValid
                ? "Playbook name must be at least 2 characters"
                : undefined
            }
            isInvalid={formData.name.length > 0 && !isNameValid}
          />

          {/* Playbook Description */}
          <TextArea
            label="Description"
            placeholder="Optional: Describe what this playbook does and when to use it"
            value={formData.description}
            onValueChange={(value) => setFormData({ ...formData, description: value })}
            variant="bordered"
            size="lg"
            minRows={3}
            maxRows={6}
            description="Help yourself remember the key aspects of this playbook"
          />

          {/* Trading Philosophy */}
          <TextArea
            label="Trading Philosophy"
            placeholder="Optional: What principles guide this playbook?"
            value={formData.philosophy}
            onValueChange={(value) => setFormData({ ...formData, philosophy: value })}
            variant="bordered"
            size="lg"
            minRows={3}
            maxRows={6}
            description="Your trading beliefs and approach with this playbook"
          />

          {/* Task 3 will add pickers for confluences/entry models here */}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onPress={handleCancel}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!canSave}
          >
            {mode === 'add' ? 'Add Playbook' : 'Save Changes'}
          </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
